<template>
  <div ref="drop-zone" class="position-relative">
    <v-card
      v-if="isOverDropZone"
      variant="tonal"
      class="d-flex flex-column w-full h-full position-absolute bg-surface"
      style="z-index: 1; inset: 0; border: 2px dashed rgb(var(--v-theme-primary)); border-radius: 8px"
    >
      <v-card-text class="h-full d-flex justify-center align-center">
        <div class="ma-auto text-body-medium text-center" variant="text">
          <v-card-item class="text-body-medium">
            <v-icon class="text-medium-emphasis" size="62" icon="upload_file" />
            <div class="mt-2 text-title-large">{{ $t("uploadFiles") }}</div>
          </v-card-item>
        </div>
      </v-card-text>
    </v-card>

    <v-form ref="uploadForm" v-model="valid" :readonly="uploading" @submit.prevent>
      <v-card-text class="overflow-hidden">
        <v-file-input
          v-model="modelValue"
          :accept="acceptValidation"
          :disabled="uploading || disabled"
          :hint="hints"
          :label="$t('pickFiles')"
          :multiple="multiple"
          :rules="[maxFilesRule, maxFileSizeRule, mimeMatchesAcceptRule(accept)]"
          chips
          clearable
          persistent-hint
          single-line
          @update:model-value="uploadFiles"
        >
          <template #selection="{ fileNames }">{{ $t("uploading") }} ({{ fileNames.length }})</template>
        </v-file-input>

        <div v-show="uploadedFiles.length || files?.length">
          <v-divider class="my-2" />
          <v-list-subheader>{{ $t("uploadedFiles") }} ({{ uploadedFiles.length }})</v-list-subheader>

          <!-- combining already uploaded files (FileData) and to-be-uploaded files (File) in one array for smooth transitions -->
          <transition-group name="file-slide" tag="v-list" class="overflow-hidden" max-height="300">
            <v-list-item
              v-for="file in [...(files ?? []), ...uploadedFiles]"
              :key="'name' in file ? file.name : `${file.description}.${file.extension}`"
            >
              <template #prepend>
                <v-avatar
                  v-if="!uploading && 'name' in file"
                  v-tooltip="errorMessage"
                  variant="tonal"
                  icon="error"
                  size="small"
                  color="error"
                />
                <v-avatar
                  v-if="resizing && !isResized(file)"
                  v-tooltip="$t('resizing')"
                  variant="tonal"
                  icon="cached"
                  size="small"
                  color="primary"
                >
                  <span class="material-symbols-outlined" style="font-size: 1rem">resize</span>
                </v-avatar>

                <v-avatar
                  v-else-if="uploading"
                  v-tooltip="$t('uploading')"
                  variant="tonal"
                  icon="arrow_upward"
                  size="small"
                  color="secondary"
                />
                <v-avatar v-else variant="tonal" icon="check" size="small" color="success" />
              </template>

              <template #default>
                <span v-if="'name' in file">{{ file.name }}</span>
                <span v-else>{{ file.description }}.{{ file.extension }}</span>
              </template>

              <template #subtitle>
                <span class="text-body-small">{{ prettyBytes(file.size) }}</span>
              </template>

              <template v-if="(uploading && 'name' in file) || (canDelete && 'description' in file)" #append>
                <v-progress-circular v-if="uploading && 'name' in file" class="mr-4" indeterminate width="3" color="primary" />
                <v-btn
                  v-if="canDelete && 'description' in file"
                  icon="delete"
                  density="comfortable"
                  color="error"
                  :disabled="deleting"
                  rounded
                  @click="() => onDelete(file._id)"
                />
              </template>
            </v-list-item>
          </transition-group>
        </div>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn v-if="!uploading && !uploadedFiles.length" color="secondary" class="custom-hover-secondary" @click="onCancel">{{
          $t("cancel")
        }}</v-btn>
        <v-btn
          variant="flat"
          color="primary"
          class="custom-hover-primary"
          type="submit"
          :disabled="!uploadedFiles.length"
          :loading="uploading"
          @click="onCancel"
          >{{ $t("save") }}
        </v-btn>
      </v-card-actions>
    </v-form>
  </div>
</template>

<script setup lang="ts">
const { t: $t } = useI18n()

import prettyBytes from "pretty-bytes"

const $i18n = useI18n()

const mainStore = useMainStore()
const authStore = useAuthStore()
const imageResizeService = useImageResizeService()
const { currentOrganization } = storeToRefs(authStore)

const uploadForm = useTemplateRef("uploadForm")

const emit = defineEmits<{
  (event: "upload", value: Array<FileData>): void
  (event: "delete", fileId: string): void
  (event: "ok"): void
}>()

const {
  accept = undefined,
  multiple = false,
  createFunction = undefined,
  deleteFunction = undefined,
  disabled = false,
} = defineProps<{
  accept?: string
  multiple?: boolean
  createFunction?: (files: Array<File>) => Promise<Array<FileData>>
  deleteFunction?: (fileId: string) => Promise<unknown>
  disabled?: boolean
}>()

const dropZoneRef = useTemplateRef("drop-zone")

const onDrop = async (droppedFiles: Array<File> | null) => {
  if (!droppedFiles) {
    return
  }

  files.value = droppedFiles
  return uploadFiles()
}

const { isOverDropZone } = useDropZone(dropZoneRef, {
  onDrop,
  // specify the types of data to be received.
  dataTypes: accept?.includes("*") ? [] : accept?.replace(" ", "").split(","),
  // control multi-file drop
  multiple: true,
  // whether to prevent default behavior for unhandled events
  preventDefaultForUnhandled: false,
})

const uploadedFiles = ref<Array<FileData>>([])
// The modelValue ref holds either a single File, an array of Files, or undefined.
const modelValue = ref<File | Array<File> | undefined>()
// File refs for the uploaded files and the current model value
const files = computed<Array<File> | undefined>({
  get: () => {
    if (!modelValue.value) {
      return []
    }

    return Array.isArray(modelValue.value) ? modelValue.value : [modelValue.value]
  },
  set: (newFiles: Array<File> | undefined) => {
    if (!newFiles || !newFiles.length) {
      modelValue.value = undefined
      return
    }
    modelValue.value = multiple ? newFiles : newFiles[0]
  },
})

const resizing = ref(false)
const uploading = ref(false)
const deleting = ref(false)
const valid = ref(false)
const errorMessage = ref<string | undefined>()

const { maxFiles, maxFileSize, maxFilesRule, maxFileSizeRule, mimeMatchesAcceptRule } = useFileUploadValidation()

const canDelete = computed(() => !!deleteFunction && authStore.hasScope(currentOrganization.value!, AuthScope.CAN_DELETE_FILES))

const acceptValidation = computed(() => {
  // If accept value is image/* we should accept all images.
  // However, Android 12+ shows the file browser instead of the camera/browser option.
  // When we remove the accept and verify this manually Android does show the camera or browser choice.
  // https://support.google.com/chrome/thread/253908853/android-14-camera-tile-removed-from-photo-picker?hl=en
  if (!accept || accept.endsWith("*")) {
    return undefined
  }
  return accept
})

const hints = computed(() =>
  [multiple && $t("maxFiles", { max: maxFiles }), $t("maxFileSize", { max: prettyBytes(maxFileSize) })].filter(Boolean).join(", "),
)

const resizedFiles = ref<Map<number, File>>(new Map())
const isResized = (file: File | FileData) => {
  if (!imageResizeService) return true
  if ("name" in file) {
    const index = files.value?.indexOf(file)
    return index !== undefined && index !== -1 && resizedFiles.value.has(index)
  }
  return false
}

const uploadFiles = async () => {
  const { valid: isFormValid } = (await uploadForm.value?.validate()) ?? { valid: false }

  if (!isFormValid || !files.value?.length) {
    return
  }

  // Reset the list of uploaded file(s) if not in multiple mode, so only the latest file is kept
  if (!multiple) uploadedFiles.value = []

  useExplicitSave(mainStore.notify, $i18n, async () => {
    uploading.value = true

    try {
      if (imageResizeService && files.value) {
        resizing.value = true
        resizedFiles.value = new Map<number, File>()
        for await (const result of imageResizeService.resizeAll(files)) {
          if (result.success) {
            resizedFiles.value.set(result.index, result.file)
          }
        }
        resizing.value = false
      }

      if (!createFunction) {
        throw new Error("No create function provided")
      }

      const newFiles = await createFunction(files.value!)

      emit("upload", newFiles)
      uploadedFiles.value = [...newFiles, ...uploadedFiles.value]
      files.value = undefined
    } catch (e) {
      errorMessage.value = getErrorMessage(e)
      console.error(errorMessage)
      throw e
    } finally {
      uploading.value = false
      resizing.value = false
    }
  })
}

const onDelete = (fileId: string) => {
  if (!deleteFunction) {
    throw new Error("No delete function provided")
  }

  deleting.value = true

  useExplicitSave(
    mainStore.notify,
    $i18n,
    async () => {
      await deleteFunction(fileId)
      uploadedFiles.value = uploadedFiles.value.filter((file) => file._id !== fileId)
      emit("delete", fileId)
    },
    "remove",
  ).finally(() => {
    deleting.value = false
  })
}

const onCancel = () => {
  emit("ok")
}
</script>
<style lang="scss">
.file-slide-leave-active {
  position: absolute !important;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1;
}

.file-slide-enter-active,
.file-slide-leave-active {
  transition:
    transform 300ms ease,
    opacity 300ms ease;
}

.file-slide-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.file-slide-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}

.file-slide-move {
  transition: transform 300ms ease;
}
</style>
